/**
 * An in-memory implementation of the {@link Storage} interface.
 *
 * This class provides a simple key-value storage mechanism that mimics the
 * browser's `localStorage` API, but stores all data in memory. Data will be
 * lost when the instance is destroyed.
 *
 */
class InMemoryStorage implements Storage {
  // Internal storage object for key-value pairs.
  private store: Record<string, string> = {};

  /**
   * Returns the number of key/value pairs currently present in the storage.
   */
  get length(): number {
    // Count the number of keys in the store.
    return Object.keys(this.store).length;
  }

  /**
   * Removes all key/value pairs from the storage.
   */
  clear(): void {
    // Reset the store to an empty object.
    this.store = {};
  }

  /**
   * Returns the value associated with the given key, or `null` if the key does not exist.
   * @param key - The key whose value to retrieve.
   * @returns The value associated with the key, or `null` if not found.
   */
  getItem(key: string): string | null {
    // Check if the key exists in the store and return its value.
    return Object.prototype.hasOwnProperty.call(this.store, key) ? this.store[key] : null;
  }

  /**
   * Returns the name of the key at the specified index, or `null` if the index is out of bounds.
   * @param index - The index of the key to retrieve.
   * @returns The key at the specified index, or `null` if not found.
   */
  key(index: number): string | null {
    // Get all keys and return the one at the given index.
    const keys = Object.keys(this.store);
    return keys[index] ?? null;
  }

  /**
   * Removes the key/value pair with the specified key from the storage.
   * @param key - The key to remove.
   */
  removeItem(key: string): void {
    // Delete the key from the store.
    delete this.store[key];
  }

  /**
   * Adds or updates a key/value pair in the storage.
   * @param key - The key to set.
   * @param value - The value to associate with the key.
   */
  setItem(key: string, value: string): void {
    // Set the value for the given key.
    this.store[key] = value;
  }
}

export const inMemoryStorage = new InMemoryStorage();
